import useUrlState from '@ahooksjs/use-url-state'
import { useRequest, useTitle } from 'ahooks'
import { Button, Spin, Tabs } from 'antd'
import { useAtom } from 'jotai'
import { assign, merge } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RouteChildrenProps, useHistory } from 'react-router-dom'

import { hasPointPagePermission, LockIcon, NoPermissionPage, PERMISSIONS } from '@/components'
import { PpFileContainer } from '@/components/files'
import { ReportContainer } from '@/components/reports/report.container'
import { ResourceAudit } from '@/components/resource-audit'
import { BigProjectResourceDashboard } from '@/components/user-resource-dashboard/big-project/big-project-resource-dashboard'
import { SpecialProjectWorkResourceContainer } from '@/components/work-resource/special-project/special-project-work-resource.container'
import { Header } from '@/modules/header'
import { userInfoAtom } from '@/modules/user'
import { PAGE_RESOURCE_POINTS_ENUM, ResourceType, ResourceUser } from '@/schemas'
import { useAxiosWithAtom } from '@/utils'
import { ga, traceEvent } from '@/utils/ga'

import { FlowStatusButton } from '../flow-status/flow-status-button.component'
import { financialOperation, productionIPDCommittee } from '../production.atom'
import { Production } from '../production.schema'
import { ProductionHeaderContainer } from './production-header/production-header.container'
import { ProductionJQLContainer } from './production-jql/production-jql.container'
import { ProductionSummaryContainer } from './production-summary'

const { TabPane } = Tabs

export const ProductionDetailContainer: React.FC<RouteChildrenProps<{ selfId: string }>> = ({ match }) => {
  const { t } = useTranslation()

  const ref = React.useRef<{
    workResourceRefresh?: () => void
    auditRefresh?: () => void
  }>()
  const selfId = match!.params.selfId
  const reportRef = React.useRef<{
    entryTime: number
    name?: string
    entry?: string
    users?: ResourceUser[]
  }>({
    entryTime: +new Date(),
    users: [],
  })

  const { data: production, refresh } = useRequest<Production>(
    {
      url: `/api/production/detail/${selfId}`,
    },
    {
      onError() {
        history.push('/production')
      },
    }
  )

  const { data: IPDCommittee } = useAxiosWithAtom(productionIPDCommittee, {
    url: '/api/production/ipd/committee',
  })
  const { data: financialOperationAdmin } = useAxiosWithAtom(financialOperation, {
    url: '/api/production/admin/financial-operation',
  })

  useTitle(production ? production.name : document.title, {
    restoreOnUnmount: true,
  })

  const [users, setUsers] = React.useState<ResourceUser[]>()
  const [isShowingHistory, setIsShowingHistory] = React.useState(false)

  React.useEffect(() => {
    if (users) {
      // reportRef.current = assign(reportRef.current, { users })
    }
  }, [users])
  const [viewer] = useAtom(userInfoAtom)

  const reportExposeTime = () => {
    const report = reportRef.current
    if (report.name && report.entry && report.entryTime) {
      ga('event', traceEvent.detailPage.events.expose_time_production_detail, {
        event_label: report.name,
        event_value: Math.round((+new Date() - report.entryTime) / 1000),
        event_category: report.entry,
        action: traceEvent.detailPage.action,
        target: traceEvent.detailPage.targets.ef_pmo_production_detail,
        payload: report.users
          ? report.users.some((user: ResourceUser) => user.email === viewer.email)
            ? '1'
            : '0'
          : '',
      })
    }
  }

  React.useEffect(() => {
    const entry = window.sessionStorage.getItem('entry')
    reportRef.current = assign(reportRef.current, {
      entry: entry || 'ef_pmo_productions',
    })
    sessionStorage.removeItem('entry')

    window.addEventListener('beforeunload', reportExposeTime)
    return () => {
      window.removeEventListener('beforeunload', reportExposeTime)
      reportExposeTime()
    }
  }, [])

  React.useEffect(() => {
    if (production) {
      reportRef.current = assign(reportRef.current, { name: production.name })
    }
  }, [production])

  const history = useHistory()
  const [state, setState] = useUrlState(
    { tab: new URLSearchParams(window.location.search).get('tab') || 'detail' },
    {
      navigateMode: 'replace',
    }
  )

  if (!production) {
    return (
      <div className='flex h-full justify-center items-center'>
        <Spin size='large' delay={300} />
      </div>
    )
  }

  const onTabTrace = (tab: string, productionName: string) => {
    const productionDetail = traceEvent.productionDetail
    switch (tab) {
      case 'detail':
        ga('event', productionDetail.events.click_switch_tab, {
          event_category: productionDetail.event_categories.ef_pmo_productions_detail,
          action: productionDetail.action,
          event_label: productionName,
          target: productionDetail.targets.detail,
        })
        break
      case 'report':
        ga('event', productionDetail.events.click_switch_tab, {
          event_category: productionDetail.event_categories.ef_pmo_productions_detail,
          action: productionDetail.action,
          event_label: productionName,
          target: productionDetail.targets.weekly_reports,
        })
        break
    }
  }

  return (
    <>
      <Header
        title={<ProductionHeaderContainer project={production} refreshProject={refresh} />}
        actions={[
          <div className='ml-5 flex gap-2' key='flow'>
            <FlowStatusButton
              key={'flow'}
              refresh={() => {
                refresh()
                ref.current?.workResourceRefresh?.()
                ref.current?.auditRefresh?.()
              }}
              buttonType={'primary'}
              production={production as Production & { productionVps: string[] }}
              ipdCommittee={IPDCommittee}
              financialOperationAdmin={financialOperationAdmin}
            />
          </div>,
        ]}
      />
      <Tabs
        destroyInactiveTabPane
        activeKey={state.tab}
        onChange={(tab) => {
          const keys = Object.keys(state)
          setState({
            ...merge({}, ...keys.map((key) => ({ [key]: undefined }))), // reset url params
            tab,
          })
          onTabTrace(tab, production.name)
          if (tab === 'detail') refresh()
        }}
        tabBarExtraContent={
          <Button type='link' onClick={() => setIsShowingHistory(!isShowingHistory)}>
            {isShowingHistory ? t('hide history') : t('show history')}
          </Button>
        }
      >
        <TabPane
          tab={
            <span>
              {t('overview')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.概况, PERMISSIONS.READ, production.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='detail'
        >
          {state.tab === 'detail' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.概况, PERMISSIONS.READ, production.permissions) ? (
              <ProductionSummaryContainer
                production={production}
                setUsers={setUsers}
                refresh={refresh}
                showRightPanel={isShowingHistory}
              />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.概况} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('requirement problem list')}
              {!hasPointPagePermission(
                PAGE_RESOURCE_POINTS_ENUM.需求与问题,
                PERMISSIONS.READ,
                production.permissions
              ) && <LockIcon />}
            </span>
          }
          key='list'
        >
          {state.tab === 'list' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.需求与问题, PERMISSIONS.READ, production.permissions) ? (
              <ProductionJQLContainer production={production} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.需求与问题} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('resource dashboard')}
              {!hasPointPagePermission(
                PAGE_RESOURCE_POINTS_ENUM.资源看板,
                PERMISSIONS.READ,
                production.permissions
              ) && <LockIcon />}
            </span>
          }
          key='dashboard'
        >
          {state.tab === 'dashboard' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源看板, PERMISSIONS.READ, production.permissions) ? (
              <BigProjectResourceDashboard
                selfId={production.selfId}
                resourceType={ResourceType.PRODUCTION}
                resource={production}
              />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.资源看板} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('production.resource')}
              {!hasPointPagePermission(
                PAGE_RESOURCE_POINTS_ENUM.资源管理,
                PERMISSIONS.READ,
                production.permissions
              ) && <LockIcon />}
            </span>
          }
          key='userResource'
        >
          {state.tab === 'userResource' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, PERMISSIONS.READ, production.permissions) ? (
              <SpecialProjectWorkResourceContainer
                ref={ref}
                resource={production}
                resourceType={ResourceType.PRODUCTION}
              />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.资源管理} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('reports')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.周报, PERMISSIONS.READ, production.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='report'
        >
          {state.tab === 'report' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.周报, PERMISSIONS.READ, production.permissions) ? (
              <ReportContainer
                selfId={production.selfId}
                type={production.type}
                permissions={production.permissions}
                name={production.name}
                refreshProject={refresh}
              />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.周报} />
            ))}
        </TabPane>

        <TabPane
          tab={
            <span>
              {t('file')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.文件, PERMISSIONS.READ, production.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='file'
        >
          {state.tab === 'file' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.文件, PERMISSIONS.READ, production.permissions) ? (
              <PpFileContainer pp={production} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.文件} />
            ))}
        </TabPane>

        <TabPane
          tab={
            <span>
              {t('audit.title')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.审批, PERMISSIONS.READ, production.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='audit'
        >
          {state.tab === 'audit' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.审批, PERMISSIONS.READ, production.permissions) ? (
              <ResourceAudit ref={ref} selfId={production.selfId} type={production.type} resource={production} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.审批} />
            ))}
        </TabPane>

        {/* {[ProductionFlowStatus.项目已结项].includes(production.flowStatus) ? (
          <TabPane
            tab={
              <span>
                {t('production.evaluation')}
                {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.结项, PERMISSIONS.READ, production.permissions) && (
                  <LockIcon />
                )}
              </span>
            }
            key='finish'
          >
            {state.tab === 'finish' &&
              (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.结项, PERMISSIONS.READ, production.permissions) ? (
                <ResourceEvaluationContainer onRefresh={refresh} resource={production} />
              ) : (
                <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.结项} />
              ))}
          </TabPane>
        ) : null} */}
      </Tabs>
    </>
  )
}
